import { isValidIBAN } from 'ibantools'
import { DateTime } from 'luxon'
import { z, ZodIssueCode } from 'zod'
import { FinnishSSN } from '@kenno/validators'

import {
  ConfirmOperationType,
  ContractStatus,
  ContractTerminationDestination,
  ContractTerminationReason,
  ContractToTenantRole,
  ContractType,
  LegacyContractTerminationReason,
  ResidentAppErrorCode,
  ResidentNotificationCategory,
  ResidentNotificationPriority,
  ResidentUserAuthLevel,
  TenantEventStatus,
  TerminationDisallowedReason,
} from '../types'
import { isFinnishIban } from '../bankAccountUtils'
import {
  type Beneficiary,
  BeneficiarySchema,
  BulletinResponseBodySchema,
  CollateralSchema,
  type PendingOperationsResponseBody,
  PendingOperationsResponseBodySchema,
  PhoneNumberSchema,
  type RejectPendingOperationResponseBody,
  RejectPendingOperationResponseBodySchema,
} from '../generated/residentApiSchemas'
import { validationErrors } from './validationErrors'
import {
  BICFromCoreSchema,
  BICSchema,
  IBANFromCoreSchema,
  IBANSchema,
} from './customFormatSchemas'

export { validationErrors }
export { IBANSchema, IBANFromCoreSchema, BICSchema, BICFromCoreSchema }

export const isEmpty = (value: string | null | undefined) =>
  !value || value.trim() === ''

export const nonEmptyString = z.string().min(1)
export const emptyString = z.literal('')

export const numberSchema = z.preprocess((val) => {
  if (typeof val === 'string') return parseInt(val, 10)
  return val
}, z.number())

const ssnNormalized = z
  .string()
  .trim()
  .transform((s) => s.replace(/\s+/g, '').toUpperCase())

export const socialSecurityNumber = ssnNormalized.refine(
  (v) => FinnishSSN.validate(v),
  { message: validationErrors.invalid_ssn }
)

const stringToJSONSchema = <T extends z.ZodTypeAny>(
  schema: T
): z.ZodType<z.infer<T>> =>
  z
    .string()
    .transform((str, ctx) => {
      try {
        return JSON.parse(str)
      } catch (e) {
        ctx.addIssue({ code: 'custom', message: 'Invalid JSON' })
        return z.NEVER
      }
    })
    .pipe(schema)

export const coreApiOutputEmailSchema = z
  .string()
  .email({ message: validationErrors.invalid_email })

export enum FileContentType {
  JPG = 'image/jpeg',
  PNG = 'image/png',
  GIF = 'image/gif',
  PDF = 'application/pdf',
  MP4 = 'video/mp4',
  MOV = 'video/quicktime',
}

export enum FileType {
  IMAGE = 'image',
  DOCUMENT = 'document',
  VIDEO = 'video',
}

export const verificationCodeRegex = /^\d{6}$/

export const isValidISODate = (dateString: string) =>
  DateTime.fromFormat(dateString, 'yyyy-MM-dd').isValid

export const isDateTodayOrInFuture = (dateString: string) =>
  DateTime.fromFormat(dateString, 'yyyy-MM-dd').startOf('day') >=
  DateTime.now().startOf('day')

export const isDateAtMostYearInFuture = (dateString: string) =>
  DateTime.fromFormat(dateString, 'yyyy-MM-dd').startOf('day') <=
  DateTime.now().plus({ month: 12 }).startOf('day')

export const ISODateSchema = z
  .string()
  .refine((value) => isValidISODate(value), {
    message: validationErrors.invalid_date,
  })

export const genericSuccessResponseSchema = z.object({
  success: z.boolean(),
  warning: z.string().optional(),
})

export const errorSchema = z.object({ error: z.string() })
export const changePasswordResponseSchema = genericSuccessResponseSchema

export const sendChangePasswordResetCodeResponseSchema =
  genericSuccessResponseSchema
export type SendChangePasswordCodeResponse = z.infer<
  typeof sendChangePasswordResetCodeResponseSchema
>

export const logoutResponseSchema = genericSuccessResponseSchema
export type LogoutResponse = z.infer<typeof logoutResponseSchema>

export const removeUserResponseSchema = genericSuccessResponseSchema
export type RemoveUserResponse = z.infer<typeof removeUserResponseSchema>

export const verifyEmailInputSchema = z.object({
  email: z.string().email(),
  newPassword: z.string(),
})
export type VerifyEmailInput = z.infer<typeof verifyEmailInputSchema>

export const verifyEmailResponseSchema = z.object({
  cognitoId: z.string(),
})
export type VerifyEmailResponse = z.infer<typeof verifyEmailResponseSchema>

export const forgotPasswordResponseSchema = genericSuccessResponseSchema
export type ForgotPasswordResponse = z.infer<
  typeof forgotPasswordResponseSchema
>

export const forgotPasswordInputSchema = z.object({
  email: z.string().email(),
})
export type ForgotPasswordInput = z.infer<typeof forgotPasswordInputSchema>

export const setVerificationCodeEmailRequestSchema = z.object({
  email: z.string().email(),
})

export const setVerificationCodeInputSchema = z.object({
  verificationCode: z
    .string()
    .min(6)
    .refine((value) => verificationCodeRegex.test(value), {
      message: validationErrors.invalid_pin,
    }),
  email: z.string().email(),
})
export type SetVerificationCodeInput = z.infer<
  typeof setVerificationCodeInputSchema
>

export const setVerificationCodeResponseSchema = z.object({
  success: z.boolean(),
})
export type SetVerificationCodeResponse = z.infer<
  typeof setVerificationCodeResponseSchema
>

export const verifyEmailSchema = z.object({
  email: z.string().email(),
  newPassword: z.string().min(8),
})

const signUpStateSchema = z.object({
  resetOldUser: z.boolean(),
  traceId: z.string().optional(),
})

export const signUpCallbackSchema = z
  .object({
    code: z.string().min(1).optional(),
    state: stringToJSONSchema(signUpStateSchema),
    error: z.string().optional(),
  })
  .refine(
    (data) => {
      const hasCode = !!data.code
      const hasError = !!data.error
      return hasCode !== hasError // XOR: exactly one must be present
    },
    {
      message: 'Either code or error must be present, but not both',
    }
  )

export const forgotPasswordVerifyEmailSchema = z.object({
  email: z.string().email(),
})

export const confirmResetPasswordInputSchema = z.object({
  newPassword: z.string(),
  resetCode: z.string().refine((value) => verificationCodeRegex.test(value), {
    message: validationErrors.invalid_pin,
  }),
})
export type ConfirmResetPasswordInput = z.infer<
  typeof confirmResetPasswordInputSchema
>

export const confirmForgotPasswordResponseSchema = genericSuccessResponseSchema
export type ConfirmForgotPasswordResponse = z.infer<
  typeof confirmForgotPasswordResponseSchema
>

export const confirmForgotPasswordInputSchema = z.object({
  email: z.string().email(),
  newPassword: z.string(),
  resetCode: z.string().refine((value) => verificationCodeRegex.test(value), {
    message: validationErrors.invalid_pin,
  }),
})
export type ConfirmForgotPasswordInput = z.infer<
  typeof confirmForgotPasswordInputSchema
>

export const confirmResetPasswordResponseSchema = genericSuccessResponseSchema
export type ConfirmResetPasswordResponse = z.infer<
  typeof confirmResetPasswordResponseSchema
>

export const resetPasswordInputSchema = z.object({
  newPassword: z.string().min(8),
})
export type ResetPasswordInput = z.infer<typeof resetPasswordInputSchema>

export const resetPasswordResponseSchema = setVerificationCodeResponseSchema
export type ResetPasswordResponse = z.infer<typeof resetPasswordResponseSchema>

export const FTNCallbackSchema = z.object({
  code: z.string().optional(),
  error: z.string().optional(),
  error_description: z.string().optional(),
})

export enum ResidentAppLocale {
  FI = 'fi',
  SV = 'sv',
  EN = 'en',
}

export enum OperationConfirmationState {
  CONFIRMED = 'confirmed',
  PENDING = 'pending',
  REJECTED = 'rejected',
}

const resetOldUserQueryStringProp = z
  .string()
  .transform((str) => str === 'true')
  .optional()
  .default('false')

export const registrationEntryLinkRequestSchema = z.object({
  locale: z.nativeEnum(ResidentAppLocale),
  resetOldUser: resetOldUserQueryStringProp,
})

export const operationConfirmationStateResponseSchema = z.object({
  state: z.nativeEnum(OperationConfirmationState),
})

export const resumePendingOperationRequestSchema = z.object({
  nonce: z.string().uuid(),
  locale: z.nativeEnum(ResidentAppLocale),
})

export type ResumePendingOperationRequest = z.infer<
  typeof resumePendingOperationRequestSchema
>

export const confirmOperationCallbackSchema = z.object({
  code: z.string().optional(),
  error: z.string().optional(),
  state: stringToJSONSchema(
    z.object({
      refreshToken: z.string(),
      contractUUID: z.string().uuid(),
      operationType: z.nativeEnum(ConfirmOperationType),
      traceId: z.string().optional(),
      nonce: z.string(),
    })
  ),
})

export const registrationEntryLinkResponseSchema = z.object({
  link: z.string(),
})

export const confirmOperationEntryLinkResponseSchema = z.object({
  link: z.string(),
  nonce: z.string(),
})

export const loginInputSchema = z.object({
  username: z.string().email(),
  password: z.string().min(8),
})
export type LoginInput = z.infer<typeof loginInputSchema>

export const loginResponseSchema = genericSuccessResponseSchema
export type LoginResponse = z.infer<typeof loginResponseSchema>

export const errorResponseSchema = z.object({
  error: z.nativeEnum(ResidentAppErrorCode),
})

export const userSchema = z.object({
  uuid: z.string().uuid(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: coreApiOutputEmailSchema.nullable(),
  phoneNumber: z.string().nullable(),
  cognitoId: z.string().nullable(),
  marketingAllowed: z.boolean().optional(),
  communicationBySms: z.boolean().optional(),
  communicationByEmail: z.boolean().optional(),
  eServicesConsent: z.boolean(),
})

export const userPermissionsRowSchema = z.object({
  contractUuid: z.string().optional(),
  tenantRoles: z.array(
    z.union([
      z.literal('tenant'),
      z.literal('payer'),
      z.literal('contractParty'),
    ])
  ),
})

export const residentUserAuthLevelSchema = z.nativeEnum(ResidentUserAuthLevel)

export const userPermissionsSchema = z.object({
  authLevel: residentUserAuthLevelSchema,
  contracts: z.array(userPermissionsRowSchema),
})

/**
 * Resident app response: the permissions from core plus whether the user must
 * identify themselves before an operation can be confirmed.
 */
export const userPermissionsResponseSchema = userPermissionsSchema.extend({
  identificationRequired: z.boolean(),
})
export type UserPermissionsResponse = z.infer<
  typeof userPermissionsResponseSchema
>

export const userUpdateSchema = z.object({
  email: coreApiOutputEmailSchema,
  phoneNumber: PhoneNumberSchema,
  marketingAllowed: z.boolean().optional(),
  communicationByEmail: z.boolean().optional(),
  communicationBySms: z.boolean().optional(),
  eServicesConsent: z.boolean().optional(),
})

export const personTenantSchema = z.object({
  id: z.number(),
  tenantUuid: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  contractUuid: z.string().uuid(),
  tenantRole: z.nativeEnum(ContractToTenantRole),
  isContractParty: z.boolean(),
  isPayer: z.boolean(),
  email: z.union([emptyString, coreApiOutputEmailSchema]).nullable(),
  phoneNumber: z.string().nullable(),
  nonDisclosure: z.boolean().optional(),
  isAdult: z.boolean(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  hasResidentUser: z.boolean(),
  tenantTerminationDate: z.union([z.string(), z.null()]),
})

export const companyTenantSchema = z.object({
  id: z.number(),
  tenantUuid: z.string().uuid(),
  companyName: z.string(),
  contractUuid: z.string().uuid(),
  tenantRole: z.nativeEnum(ContractToTenantRole).nullable(),
  isContractParty: z.boolean(),
  isPayer: z.boolean(),
  email: z.union([emptyString, coreApiOutputEmailSchema]).nullable(),
  phoneNumber: z.string().nullable(),
  nonDisclosure: z.boolean().optional(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  tenantTerminationDate: z.union([z.string(), z.null()]),
})

const tenantSchema = z.union([personTenantSchema, companyTenantSchema])

export const userNotificationschema = z.object({
  id: z.number(),
  contractUuid: z.string().uuid(),
  priority: z.nativeEnum(ResidentNotificationPriority),
  category: z.nativeEnum(ResidentNotificationCategory),
  notificationMeta: z
    .object({
      arrearAmount: z.string(),
    })
    .nullable(),
  isCloseable: z.boolean(),
  createdAt: z.string(),
  closedAt: z.string().optional(),
})

export const userNotificationClosedResponseSchema = z.object({
  id: z.number(),
  closedAt: z.string(),
})
export type UserNotificationClosedResponse = z.infer<
  typeof userNotificationClosedResponseSchema
>

export const userNotificationsSchema = z.array(userNotificationschema)

export const pendingOperationsSchema = PendingOperationsResponseBodySchema
export type PendingOperations = PendingOperationsResponseBody
export type PendingOperation = PendingOperationsResponseBody[number]

export const rejectPendingOperationResponseSchema =
  RejectPendingOperationResponseBodySchema
export type RejectPendingOperationResponse = RejectPendingOperationResponseBody

export const contractTenantsSchema = z.array(tenantSchema)

export const contractCollateralSchema = CollateralSchema

export const propertyCompanyAddressSchema = z.object({
  streetAddressLine1: z.string(),
  streetAddressLine2: z.string().nullable(),
  postalCode: z.string(),
  city: z.string(),
})

export const featureFlagSchema = z.object({
  removeUser: z
    .boolean()
    .describe('Show/hide red remove user button from user info page'),
  paymentInfo: z
    .boolean()
    .describe('Show/hide payment info section from navbar'),
})

export type FeatureFlag = z.infer<typeof featureFlagSchema>

export const cloudwatchRumConfigSchema = z.object({
  enabled: z.boolean(),
  applicationId: z.string(),
  applicationVersion: z.string(),
  region: z.string(),
  identityPool: z.string(),
  endpoint: z.string(),
  sampleRate: z.number(),
  siteId: z.string(),
  tenantId: z.string(),
})

export type CloudwatchRumConfig = z.infer<typeof cloudwatchRumConfigSchema>

export const contractTenantSchema = z.object({
  isPayer: z.boolean(),
  isContractParty: z.boolean(),
  tenantRole: z.nativeEnum(ContractToTenantRole),
  id: z.number(),
})

export const contractSchema = z.object({
  uuid: z.string().uuid(),
  realPropertyName: z.string(),
  contractType: z.nativeEnum(ContractType),
  status: z.nativeEnum(ContractStatus),
  startDate: z.string(),
  endDate: z.string().nullable(),
  postalAddress: z.object({
    streetAddressLine1: z.string(),
    streetAddressLine2: z.string().nullable(),
    postalCode: z.string(),
    city: z.string(),
  }),
  addressDetails: z.object({
    staircaseLetter: z.string().nullable(),
    apartmentNumber: z.string().nullable(),
  }),
  terminationRequest: z
    .object({
      status: z.nativeEnum(TenantEventStatus),
      createdAt: z.string(),
    })
    .optional(),
  terminationDisallowedReason: z
    .nativeEnum(TerminationDisallowedReason)
    .optional(),
  modificationRequest: z
    .object({
      status: z.nativeEnum(TenantEventStatus),
      createdAt: z.string(),
    })
    .optional(),
  referenceNumber: z.string({
    description: 'Laskuissa käytettävä viitenumero',
  }),
  propertyCompanyName: z
    .string({
      description: 'Sopimuksen kiinteistöyhtiöosapuoli',
    })
    .nullable(),
  collateral: contractCollateralSchema.optional(),
  propertyCompanyAddress: propertyCompanyAddressSchema.nullable(),
  tenant: contractTenantSchema,
  isFixedTerm: z.boolean().optional(),
})

export type PropertyCompanyAddressType = z.infer<
  typeof propertyCompanyAddressSchema
>

export const existingBeneficiarySchema = BeneficiarySchema

export const beneficiariesSchema = z.array(existingBeneficiarySchema)

export type { Beneficiary }

export const terminateContractResponseSchema = z.object({
  id: z.number(),
  status: z.string(),
})

export const initConfirmOperationResponseSchema = z.object({
  link: z.string(),
})
export type InitConfirmOperationResponse = z.infer<
  typeof initConfirmOperationResponseSchema
>

export const findConfirmOperationStateResponseSchema = z.object({
  state: z.nativeEnum(OperationConfirmationState),
})

export const ConfirmOperationResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
})

const percentageField = z.number().int().min(0).max(100)

const contractBeneficiarySchema = z.object({
  beneficiaryName: z.string().max(100).nullable(),
  iban: IBANSchema.nullable(),
  bic: BICSchema.nullable(),
  returnPercentage: percentageField.nullable(),
  rooReturnPercentage: percentageField.nullable(),
  phoneNumber: z.string().max(15).nullable(),
  email: z.string().email().max(100).nullable(),
  removeExistingBeneficiary: z.boolean().optional(),
})

export const contractBeneficiaryInputSchema = contractBeneficiarySchema.extend({
  existingBeneficiaryId: z.number().nullable(),
  removeExistingBeneficiary: z.boolean(),
  contractToTenantAssociationId: z.number().nullable(),
  returnPercentage: percentageField,
})

export const newAddressSchema = z
  .object({
    recipientName: z.string().max(100).optional(),
    streetAddress: z.string().max(100).optional(),
    streetAddressLine2: z.string().max(100).optional(),
    postalCode: z.string().optional(),
    city: z.string().max(100).optional(),
    countryName: z.string().max(100).optional(),
    countryCode: z.string().length(2).toUpperCase().optional(),
    countrySubdivision: z.string().max(100).nullable().optional(),
  })
  // postal code extra validation for Finland
  .superRefine((data, ctx) => {
    const { countryCode, postalCode } = data
    if (countryCode !== 'FI') {
      return
    }
    if (postalCode === undefined || postalCode === '') {
      return
    }
    if (!/^\d{5}$/.test(postalCode)) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: validationErrors.invalid_postal_code,
        path: ['postalCode'],
      })
    }
  })

export const verificationCodeInputSchema = z.object({
  verificationCode: z
    .string()
    .refine((value) => verificationCodeRegex.test(value), {
      message: validationErrors.invalid_pin,
    }),
  email: z.string().email(),
})

export const resendVerificationCodeInputSchema = z.object({
  email: z.string().email(),
})
export type ResendVerificationCodeInput = z.infer<
  typeof resendVerificationCodeInputSchema
>

export const resendVerifictionCodeResponseSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
})
export type ResendVerificationCodeResponse = z.infer<
  typeof resendVerifictionCodeResponseSchema
>

export const verificationResetPasswordSchema = z.object({
  newPassword: z.string(),
})

export const verifyPasswordSchema = z.object({
  verificationCode: z.string(),
  newPassword: z.string(),
})

export const forgotPasswordRequestSchema = z.object({
  email: z.string().email(),
})

export const confirmPasswordResetInputSchema = z.object({
  newPassword: z.string(),
  resetCode: z.string().refine((value) => verificationCodeRegex.test(value), {
    message: validationErrors.invalid_pin,
  }),
})

export const contractTerminationRequestSchema = z.object({
  id: z.number(),
  movingOutDate: ISODateSchema.nullable(),
  givingContactInfoAllowed: z.boolean(),
  masterKeyAccessAllowed: z.boolean(),
  hasPetsInApartment: z.boolean(),
  terminationReason: z.union([
    z.nativeEnum(ContractTerminationReason),
    z.nativeEnum(LegacyContractTerminationReason),
  ]),
  movingDestination: z.nativeEnum(ContractTerminationDestination),
  endDate: ISODateSchema,
  displaysSelf: z.boolean(),
  presenterTenantUuid: z.string().uuid().nullable().optional(),
  displayStartDate: z
    .union([ISODateSchema, z.literal(''), z.null()])
    .optional(),
  displayEndDate: z.union([ISODateSchema, z.literal(''), z.null()]).optional(),
  additionalDetails: z.string(),
  files: z.array(z.string()),
  newAddress: newAddressSchema.optional(),
  isCollateral: z.boolean().nullable(),
  collateralLender: z.string().nullable(),
  status: z.nativeEnum(TenantEventStatus),
  beneficiaries: z.array(contractBeneficiarySchema),
})

export type ContractTerminationRequest = z.infer<
  typeof contractTerminationRequestSchema
>

export const terminateContractInputSchema = z
  .object({
    contractType: z.nativeEnum(ContractType),
    movingOutDate: z
      .string()
      .refine(isDateTodayOrInFuture, {
        message: validationErrors.too_small_date,
      })
      .refine(isValidISODate, {
        message: validationErrors.invalid_date,
      })
      .refine(isDateAtMostYearInFuture, {
        message: validationErrors.too_big_date,
      })
      .nullable(),
    endDate: ISODateSchema.refine(isDateAtMostYearInFuture, {
      message: validationErrors.too_big_date,
    }),
    displaysSelf: z.boolean(),
    presenterTenantUuid: z.string().uuid().nullable().optional(),
    displayStartDate: ISODateSchema.optional(),
    displayEndDate: ISODateSchema.optional(),
    additionalDetails: z.string().max(1000),
    givingContactInfoAllowed: z.boolean(),
    masterKeyAccessAllowed: z.boolean(),
    hasPetsInApartment: z.boolean(),
    terminationReason: z.nativeEnum(ContractTerminationReason),
    movingDestination: z.nativeEnum(ContractTerminationDestination).optional(),
    fileUuids: z.array(z.string().uuid()),
    newAddress: newAddressSchema.optional(),
    collater: contractCollateralSchema.optional(),
    isCollateral: z.boolean().nullable(),
    collateralLender: z.string().min(1).nullable(),
    beneficiaries: z.array(contractBeneficiaryInputSchema).min(0).max(4),
    contractEndDate: z.string().nullable().optional(), // The contract's actual end date for validation
  })
  .superRefine((data, ctx) => {
    const {
      contractType,
      movingOutDate,
      endDate,
      isCollateral,
      collateralLender,
      beneficiaries,
      newAddress,
      contractEndDate,
    } = data

    // moveOutDate must be before endDate
    const eDate = DateTime.fromFormat(endDate, 'yyyy-MM-dd')
    const mDate = movingOutDate
      ? DateTime.fromFormat(movingOutDate, 'yyyy-MM-dd')
      : null
    if (mDate && mDate > eDate) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: validationErrors.move_date_after_end_date,
        path: ['movingOutDate'],
      })
    }

    const zone = 'Europe/Helsinki'
    const maxTerminationDate = DateTime.now()
      .setZone(zone)
      .plus({ years: 1 })
      .endOf('day')

    // RENTAL: endDate validation
    // - For open-ended contracts: earliest end date is end of next month
    // - For fixed-term contracts: allow early termination with any date
    if (contractType === ContractType.RENTAL) {
      const minRentalTerminationDate = DateTime.now()
        .setZone(zone)
        .plus({ months: 1 })
        .endOf('month')
        .startOf('day')

      const end = DateTime.fromFormat(endDate, 'yyyy-MM-dd', { zone })
        .setZone(zone)
        .endOf('day')

      if (!end.isValid) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          message: validationErrors.invalid_date,
          path: ['endDate'],
        })
      } else {
        const isFixedTerm = Boolean(contractEndDate?.trim())

        if (isFixedTerm && contractEndDate) {
          // Fixed-term contract: allow early termination with any date
          // The selected end date must not be after the contract end date
          const contractEnd = DateTime.fromISO(contractEndDate, {
            setZone: true,
          })
            .setZone(zone)
            .endOf('day')

          if (end > contractEnd) {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              message: validationErrors.too_small_date,
              path: ['endDate'],
            })
          }
        } else {
          // Open-ended contract: enforce normal minimum termination date
          const inRange =
            end >= minRentalTerminationDate && end <= maxTerminationDate

          if (!inRange) {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              message: validationErrors.too_small_date,
              path: ['endDate'],
            })
          }
        }
      }
    }

    // ASO: endDate validation
    // - For open-ended contracts: earliest end date is 3 months from now
    // - For fixed-term contracts: allow early termination (any date up to contract end date)
    if (contractType === ContractType.RIGHT_OF_OCCUPANCY) {
      const minRightOfOccupancyTerminationDate = DateTime.now()
        .setZone(zone)
        .plus({ months: 3 })
        .endOf('day')
      const end = DateTime.fromFormat(endDate, 'yyyy-MM-dd', { zone })
        .setZone(zone)
        .endOf('day')

      if (!end.isValid) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          message: validationErrors.invalid_date,
          path: ['endDate'],
        })
      } else {
        const isFixedTerm = Boolean(contractEndDate?.trim())

        if (isFixedTerm && contractEndDate) {
          // Fixed-term contract: allow early termination with any date
          // The selected end date must not be after the contract end date
          const contractEnd = DateTime.fromISO(contractEndDate, {
            setZone: true,
          })
            .setZone(zone)
            .endOf('day')

          if (end > contractEnd) {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              message: validationErrors.too_small_date,
              path: ['endDate'],
            })
          }
        } else {
          // Open-ended contract: enforce normal minimum termination date
          const inRange =
            end >= minRightOfOccupancyTerminationDate &&
            end <= maxTerminationDate

          if (!inRange) {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              message: validationErrors.too_small_date,
              path: ['endDate'],
            })
          }
        }
      }
    }

    if (
      data.contractType === ContractType.RIGHT_OF_OCCUPANCY &&
      isCollateral === null
    ) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: validationErrors.required,
        path: ['isCollateral'],
      })
    }

    // lender must be set if collateral is true
    if (isCollateral && !collateralLender) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: validationErrors.required,
        path: ['collateralLender'],
      })
    }
    // Beneficiaries validation
    const isBeneficiaryWithIban = (b: (typeof beneficiaries)[number]) => {
      const hasName = !!b.beneficiaryName?.trim()
      const hasIban = !!b.iban?.trim()
      return (hasName || hasIban) && b.removeExistingBeneficiary !== true
    }

    const beneficiariesWithIban = beneficiaries.filter(isBeneficiaryWithIban)

    const returnPercentageSum = beneficiariesWithIban.reduce(
      (acc, b) => acc + (b.returnPercentage ?? 0),
      0
    )

    if (beneficiariesWithIban.length > 0 && returnPercentageSum !== 100) {
      beneficiaries.forEach((beneficiary, index) => {
        if (isBeneficiaryWithIban(beneficiary)) {
          ctx.addIssue({
            code: ZodIssueCode.custom,
            message: validationErrors.invalid_return_percentage_sum,
            path: ['beneficiaries', index, 'returnPercentage'],
          })
        }
      })
    }

    const rooReturnPercentages = beneficiariesWithIban.map(
      ({ rooReturnPercentage }) => rooReturnPercentage
    )
    const hasRooReturnAllocation = rooReturnPercentages.some(
      (percentage) => percentage !== null
    )
    const hasInvalidRooReturnAllocation =
      rooReturnPercentages.some((percentage) => percentage === null) ||
      rooReturnPercentages.reduce<number>(
        (sum, percentage) => sum + (percentage ?? 0),
        0
      ) !== 100

    if (
      contractType === ContractType.RIGHT_OF_OCCUPANCY &&
      hasRooReturnAllocation &&
      hasInvalidRooReturnAllocation
    ) {
      beneficiaries.forEach((beneficiary, index) => {
        if (isBeneficiaryWithIban(beneficiary)) {
          ctx.addIssue({
            code: ZodIssueCode.custom,
            message: validationErrors.invalid_roo_return_percentage_sum,
            path: ['beneficiaries', index, 'rooReturnPercentage'],
          })
        }
      })
    }

    beneficiaries.forEach((b, index) => {
      const hasName = !!b.beneficiaryName?.trim()
      const hasIban = !!b.iban?.trim()

      // Skip validation if both name and IBAN are empty
      if (!hasName && !hasIban) {
        return
      }

      if (b.removeExistingBeneficiary !== true) {
        const isFirst = index === 0
        const fields: (keyof typeof b)[] =
          data.contractType === ContractType.RIGHT_OF_OCCUPANCY ||
          data.contractType === ContractType.RENTAL
            ? ['beneficiaryName', 'iban', 'returnPercentage']
            : isFirst
              ? hasIban || hasName
                ? ['beneficiaryName', 'iban', 'returnPercentage']
                : []
              : ['beneficiaryName', 'iban', 'returnPercentage']

        fields.forEach((field) => {
          if (b[field] === null || b[field] === '') {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              message: validationErrors.required,
              path: ['beneficiaries', index, field],
            })
          }
        })

        if (
          b.iban &&
          isValidIBAN(b.iban) &&
          !isFinnishIban(b.iban) &&
          (b.bic === null || b.bic === '')
        ) {
          ctx.addIssue({
            code: ZodIssueCode.custom,
            message: validationErrors.required,
            path: ['beneficiaries', index, 'bic'],
          })
        }

        if (
          b.phoneNumber &&
          !PhoneNumberSchema.safeParse(b.phoneNumber).success
        ) {
          ctx.addIssue({
            code: ZodIssueCode.custom,
            message: validationErrors.invalid_phone_number,
            path: ['beneficiaries', index, 'phoneNumber'],
          })
        }
      }
    })

    if (newAddress) {
      const requiredFields: Array<keyof typeof newAddress> = [
        'recipientName',
        'streetAddress',
        'city',
        'postalCode',
        'countryCode',
        'countryName',
      ]
      const values = requiredFields.map((field) => newAddress[field])
      if (values.every(isEmpty) || !values.some(isEmpty)) {
        return
      }
      requiredFields.forEach((field) => {
        if (isEmpty(newAddress[field])) {
          ctx.addIssue({
            code: ZodIssueCode.custom,
            message: validationErrors.required,
            path: ['newAddress', field],
          })
        }
      })
    }
  })

export type TerminateContractInput = z.infer<
  typeof terminateContractInputSchema
>

export const ContractModificationStatus = z.enum([
  'pending_tenant_approval',
  'approved',
  'pending_strong_authentication',
  'ready_for_review',
  'rejected',
])

export const contractModificationSchema = z.object({
  id: z.number(),
  uuid: z.string().uuid(),
  status: ContractModificationStatus,
  description: z.string(),
  contractId: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdByResidentUserId: z.number(),
  inEffect: z.string(),
  addTenantEvents: z.array(
    z.object({
      id: z.number(),
      email: z.string().optional(),
      socialSecurityNumber: socialSecurityNumber.optional(),
      birthDate: z.string().optional(),
      firstName: z.string(),
      lastName: z.string(),
    })
  ),
  transferTenantEvent: z
    .object({
      sourceTenantId: z.number(),
      destinationTenantId: z.number(),
      contractModificationEventId: z.number(),
    })
    .optional(),
  removeTenantEvents: z.array(
    z.object({
      removeTenantId: z.number(),
      contractModificationEventId: z.number(),
    })
  ),
})

export type ContractModificationRequest = z.infer<
  typeof contractModificationSchema
>

export const modifyContractInputSchema = z
  .object({
    inEffect: z
      .string()
      .refine(isDateTodayOrInFuture, {
        message: validationErrors.too_small_date,
      })
      .refine(isValidISODate, {
        message: validationErrors.invalid_date,
      })
      .refine(isDateAtMostYearInFuture, {
        message: validationErrors.too_big_date,
      }),
    description: z.string().optional(),
    removeTenants: z
      .array(
        z.object({
          tenantId: z.number(),
        })
      )
      .optional(),
    transfer: z
      .object({
        sourceTenantId: z.number(),
        destinationTenantId: z.number(),
      })
      .optional(),
    // Needed for validation only
    contractPartyTenantIds: z.array(z.number()),
    // Needed for validation only
    possibleContractPartyTenants: z.array(personTenantSchema),
    // Needed for validation only
    currentUserTenantId: z.number(),
    // Needed for validation only
    tenants: z.array(tenantSchema),
    // Needed for showing generic error
    genericError: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const removeTenantIds = data.removeTenants?.map((tenant) => tenant.tenantId)

    const removeAdultsWithoutResidentUser = data.tenants.some(
      (tenant) =>
        removeTenantIds?.includes(tenant.id) &&
        'isAdult' in tenant &&
        tenant.isAdult &&
        !tenant.hasResidentUser
    )

    if (removeAdultsWithoutResidentUser) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: validationErrors.terminated_adult_missing_resident_user,
        path: ['genericError'],
      })
    }

    if (
      data.contractPartyTenantIds.every((contractPartyTenantId) =>
        removeTenantIds?.includes(contractPartyTenantId)
      ) &&
      !data.transfer?.destinationTenantId
    ) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: validationErrors.required,
        path: ['transfer', 'destinationTenantId'],
      })
    }

    const possibleNewParty = data.possibleContractPartyTenants.find(
      (party) => party.id === data.transfer?.destinationTenantId
    )
    if (
      possibleNewParty &&
      !possibleNewParty.hasResidentUser &&
      !possibleNewParty.isPayer
    ) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: validationErrors.no_resident_user,
        path: ['transfer', 'destinationTenantId'],
      })
    }
    const leftContractParties = data.tenants
      .filter((tenant) => tenant.isContractParty)
      .filter((tenant) => !removeTenantIds?.includes(tenant.id))
    if (
      data.possibleContractPartyTenants.length === 0 &&
      leftContractParties.length === 0
    ) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: validationErrors.no_adult_left,
        path: ['genericError'],
      })
    }

    const otherContractPartiesToTerminate = data.tenants
      .filter((tenant) => tenant.id !== data.currentUserTenantId)
      .filter((tenant) => tenant.isContractParty)
      .filter((tenant) => removeTenantIds?.includes(tenant.id))

    if (otherContractPartiesToTerminate.length > 0) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: validationErrors.other_contract_party_termination,
        path: ['genericError'],
      })
    }
  })

export type ModifyContractInput = z.infer<typeof modifyContractInputSchema>

export const newTenantSchema = z.object({
  socialSecurityNumber,
  firstName: z.string().trim().min(1),
  lastName: z.string().trim().min(1),
  email: z.string().email().optional(),
  phoneNumber: z.string().optional(),
  birthDate: z.string().date().optional(),
})

export type NewTenantType = z.infer<typeof newTenantSchema>

export const addTenantInputSchema = z.object({
  tenants: z.array(newTenantSchema),
})

export type AddTenantInput = z.infer<typeof addTenantInputSchema>

export type Contract = z.infer<typeof contractSchema>

export const contractsSchema = z.array(contractSchema)

export const nonceSchema = z.object({ nonce: z.string() }).strict()

export const uuidSchema = z.object({ uuid: z.string().uuid() }).strict()

export const uuidWithContractSchema = z.object({
  contractUuid: z.string().uuid(),
  id: z.string(),
})

export const documentParamsSchema = z.object({
  contractUuid: z.string().uuid(),
  documentUuid: z.string().uuid(),
})

export const fileInitInputSchema = z
  .object({
    contractUuid: z.string().uuid(),
    file: z.object({
      fileName: z.string(),
      contentType: z.nativeEnum(FileContentType),
    }),
  })
  .strict()
export type FileInitInput = z.infer<typeof fileInitInputSchema>

const fileResponseBaseSchema = z.object({
  id: z.number(),
  uuid: z.string().uuid(),
  fileName: z.string(),
  fileType: z.nativeEnum(FileType),
  contentType: z.nativeEnum(FileContentType),
  createdByUserId: z.number(),
  createdAt: z.string(),
  metadata: z.object({}),
})

const fileMetadataResponseSchema = z.object({
  uploaded: z.boolean(),
  temporary: z.boolean(),
  originalFileName: z.string(),
})

const fileInitMetadataResponseSchema = fileMetadataResponseSchema.extend({
  uploadUrl: z.string(),
  uploadHeaders: z.record(z.string()),
})

export const UserBySsnResponse = z.object({
  tenantId: z.number(),
})

export const fileInitResponseSchema = fileResponseBaseSchema.extend({
  metadata: fileInitMetadataResponseSchema,
})
export type FileInitResponse = z.infer<typeof fileInitResponseSchema>

export const fileCompleteInputSchema = z
  .object({
    file: z.object({
      uuid: z.string().uuid(),
    }),
  })
  .strict()
export type FileCompleteInput = z.infer<typeof fileCompleteInputSchema>

export const fileCompleteResponseSchema = fileResponseBaseSchema.extend({
  metadata: fileMetadataResponseSchema,
})
export type FileCompleteResponse = z.infer<typeof fileCompleteResponseSchema>

export const tenantBySsnSchema = z.object({
  tenantId: z.number(),
  email: coreApiOutputEmailSchema,
  contractId: z.number().nullable(),
  residentUserId: z.number().nullable(),
})

export const operationConfirmationInitSchema = z.object({
  operationId: z.string(),
  data: z.string(), // stringified json
})

export const operationConfirmationInitResponseSchema = z.object({
  nonce: z.string(),
})

export enum IdentityProvider {
  FTN = 'ftn',
}

export const initConfirmCreateTerminationSchema = z.object({
  locale: z.nativeEnum(ResidentAppLocale),
  operationData: terminateContractInputSchema,
  provider: z.nativeEnum(IdentityProvider),
})

export type InitConfirmCreateTerminationRequest = z.infer<
  typeof initConfirmCreateTerminationSchema
>

export const initConfirmApproveTerminationSchema = z.object({
  locale: z.nativeEnum(ResidentAppLocale),
  operationType: z.nativeEnum(ConfirmOperationType),
  operationData: z.object({ terminationRequestId: z.number() }),
})

export type InitConfirmApproveTerminationRequest = z.infer<
  typeof initConfirmApproveTerminationSchema
>

export const createContractModificationResponseSchema = z.object({
  uuid: z.string().uuid(),
})

export type CreateContractModificationResponse = z.infer<
  typeof createContractModificationResponseSchema
>

export const createContractModificationEventResponseSchema = z.object({
  status: z.string(),
})

export type CreateContractModificationEventResponse = z.infer<
  typeof createContractModificationEventResponseSchema
>

export const initConfirmCreateModificationSchema = z.object({
  locale: z.nativeEnum(ResidentAppLocale),
  operationData: modifyContractInputSchema,
  provider: z.nativeEnum(IdentityProvider),
})

export type InitConfirmCreateModificationRequest = z.infer<
  typeof initConfirmCreateModificationSchema
>

export const initConfirmApproveModificationSchema = z.object({
  locale: z.nativeEnum(ResidentAppLocale),
  operationType: z.nativeEnum(ConfirmOperationType),
  operationData: z.object({ modificationRequestId: z.number() }),
})

export type InitConfirmApproveModificationRequest = z.infer<
  typeof initConfirmApproveModificationSchema
>

export const executeAddTenantSchema = z.object({
  locale: z.nativeEnum(ResidentAppLocale),
  operationData: addTenantInputSchema,
})

export type ExecuteAddTenant = z.infer<typeof executeAddTenantSchema>

export const initConfirmOperationParamsSchema = z.object({
  uuid: z.string().uuid(),
})

export const userMetadataResponseSchema = z.object({
  isImpersonator: z.boolean(),
  impersonatorEmail: z.string(),
  identityProvider: z.nativeEnum(IdentityProvider),
})

export const userUpdateResponseSchema = z.object({
  success: z.boolean(),
})

export type UserUpdateRequest = z.infer<typeof userUpdateSchema>
export type UserUpdateResponse = z.infer<typeof userUpdateResponseSchema>
export const bulletinAttachmentSchema = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
})
export type BulletinAttachment = z.infer<typeof bulletinAttachmentSchema>

export const bulletinFeaturedImageSchema = z.object({
  url: z.string(),
  alt: z.string(),
})

export type BulletinFeaturedImageType = z.infer<
  typeof bulletinFeaturedImageSchema
>

export const bulletinLanguageSchema = BulletinResponseBodySchema.shape.language

export const bulletinSchema = z.object({
  uuid: z.string().uuid(),
  title: z.string(),
  excerpt: z.string(),
  body: z.string(),
  publishedAt: z.string(),
  updatedAt: z.string().nullable(),
  author: z.string().nullable(),
  featuredImage: bulletinFeaturedImageSchema.nullable(),
  attachments: z.array(bulletinAttachmentSchema).nullable(),
  language: bulletinLanguageSchema,
  availableLanguages: z.array(bulletinLanguageSchema),
})
export type BulletinType = z.infer<typeof bulletinSchema>

export const bulletinsSchema = z.array(
  bulletinSchema.omit({ body: true, attachments: true })
)
export type Bulletins = z.infer<typeof bulletinsSchema>

export const bulletinParamsSchema = z.object({
  contractUuid: z.string().uuid(),
  bulletinUuid: z.string().uuid(),
})

export const bulletinQuerySchema = z.object({
  language: bulletinLanguageSchema.optional(),
})

export const bulletinAttachmentParamsSchema = z.object({
  contractUuid: z.string().uuid(),
  bulletinUuid: z.string().uuid(),
  attachmentUuid: z.string().uuid(),
})

export const bulletinAttachmentResponseSchema = z.object({
  url: z.string(),
})

export enum HeatingMethod {
  AIR_TO_WATER_HEAT_PUMP = 'air_to_water_heat_pump',
  DISTRICT = 'district',
  DISTRICT_AND_EXHAUST_AIR_HEAT_PUMP = 'district_and_exhaust_air_heat_pump',
  DISTRICT_AND_SOLAR = 'district_and_solar',
  ELECTRICAL = 'electrical',
  GAS = 'gas',
  GEOTHERMAL = 'geothermal',
  SOLAR = 'solar',
  OIL = 'oil',
  GEOTHERMAL_AND_SOLAR = 'geothermal_and_solar',
  NATURAL_GAS = 'natural_gas',
  NO_HEATING = 'no_heating',
  NOT_SET = 'not_set',
}

export const buildingInfoSchema = z.object({
  constructionDate: z.string(),
  floorCount: z.number().nullable(),
  realPropertyAreasTotal: z.number(),
  heatingMethod: z.nativeEnum(HeatingMethod),
  realPropertyImageUrl: z.string().nullable(),
  internetOperator: z
    .object({
      email: z.string().nullable(),
      officeName: z.string().nullable(),
      phoneNumber: z.string().nullable(),
    })
    .nullable(),
  internetProvider: z
    .object({
      operatorName: z.string(),
      internetConnection: z.string().nullable(),
      links: z.array(
        z.object({
          title: z.string(),
          url: z.string(),
        })
      ),
    })
    .nullable(),
  postalAddress: z.object({
    streetAddressLine1: z.string().nullable(),
    streetAddressLine2: z.string().nullable(),
    city: z.string().nullable(),
    postalCode: z.string().nullable(),
    countryName: z.string().nullable(),
    countryCode: z.string().nullable(),
  }),
  address: z
    .object({
      city: z.string(),
      streetName: z.string(),
      streetNumber: z.string(),
      building: z.string().nullable(),
      district: z.string().nullable(),
      districtSpecifier: z.string().nullable(),
      region: z.string().nullable(),
    })
    .nullable(),
})

export const bulletinBoardSchema = z.object({
  bulletins: z.array(bulletinSchema.omit({ body: true, attachments: true })),
  nextPage: z.number().nullable(),
})

export type BulletinBoardResponse = z.infer<typeof bulletinBoardSchema>

export const bulletinBoardQuerySchema = z.object({
  page: z.coerce.number(),
  language: bulletinLanguageSchema.optional(),
})

export type BulletinBoardQuery = z.infer<typeof bulletinBoardQuerySchema>

// Types

export type User = z.infer<typeof userSchema>
export type ContractTenant = z.infer<typeof tenantSchema>
export type UserNotifications = z.infer<typeof userNotificationsSchema>
export type UserNotification = z.infer<typeof userNotificationschema>
export type ErrorResponse = z.infer<typeof errorResponseSchema>
export type TenantBySsn = z.infer<typeof tenantBySsnSchema>
export type SetVerificationCodeEmailRequest = z.infer<
  typeof setVerificationCodeEmailRequestSchema
>

export type SetVerificationCodeRequest = z.infer<
  typeof setVerificationCodeInputSchema
>
export type ChangePasswordResponse = z.infer<
  typeof changePasswordResponseSchema
>
export type UserMetadata = z.infer<typeof userMetadataResponseSchema>
export type ContractTenantData = z.infer<typeof contractTenantSchema>

export type UserPermissions = z.infer<typeof userPermissionsSchema>
export type UserPermissionsRow = z.infer<typeof userPermissionsRowSchema>
export { ResidentUserAuthLevel }

export type BuildingInfoType = z.infer<typeof buildingInfoSchema>
